import { useFeatureFlags } from '@/contexts/FeatureFlagsContext'
import { usePlugins } from '@/contexts/PluginContext'
import { ChevronDown, FileText, Megaphone, PanelLeft, Plus, Search, ShoppingBag, Tag } from 'lucide-react'
import { useEffect, useRef, useState, type ComponentType, type PointerEvent } from 'react'
import { Link, useLocation } from 'react-router-dom'

import { cn } from '@/lib/utils'
import { isNavActive } from '@/lib/navigation'
import { settingsPages } from '@/config/sidebar'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

import { AccountMenu } from './AccountMenu'

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)

/** Sidebar row after merging the feature-flagged config with plugin items. */
type Entry =
  | { kind: 'header'; key: string; label: string }
  | {
      kind: 'link' | 'group'
      key: string
      label: string
      /** Where the row itself goes: the link, or a group's first visible child (its landing page). */
      to: string
      /** A group's landing page's own label (Settings → General), for menus that list it as a page. */
      landingLabel?: string
      icon?: ComponentType<{ className?: string }>
      /** A group's other pages, shown as sub-items while the group is active. */
      children: { to: string; label: string; nested?: boolean }[]
      bottom: boolean
    }

type NavEntry = Exclude<Entry, { kind: 'header' }>

const withSlash = (to: string) => (to.startsWith('/') ? to : `/${to}`)

function useEntries(): Entry[] {
  const { getPluginNavItems } = usePlugins()
  const { getSidebarItems } = useFeatureFlags()
  const entries: Entry[] = []
  getSidebarItems().forEach((item, index) => {
    if (item.type === 'header' && item.label) {
      entries.push({ kind: 'header', key: `h:${item.label}`, label: item.label })
    } else if (item.type === 'plugins') {
      getPluginNavItems().forEach((p) => {
        const children = (p.children ?? []).map((c) => ({ to: withSlash(c.path), label: c.label }))
        entries.push({ kind: children.length ? 'group' : 'link', key: p.path, label: p.label, to: withSlash(p.path), icon: p.icon, children, bottom: false })
      })
    } else if (item.type === 'group') {
      // Settings lists the same pages as its column: /settings expanded into its tabs, sub-pages nested.
      const pages =
        item.id === 'settings'
          ? settingsPages(item.children ?? [])
          : (item.children ?? []).flatMap((c) => (c.to ? [{ to: c.to, label: c.label ?? '', nested: false }] : []))
      const [landing, ...rest] = pages.map((c) => ({ to: withSlash(c.to), label: c.label, nested: c.nested }))
      if (!landing) return
      entries.push({
        kind: 'group',
        key: item.id || item.label || String(index),
        label: item.label ?? '',
        to: landing.to,
        landingLabel: landing.label,
        icon: item.icon,
        children: rest,
        bottom: item.placement === 'bottom',
      })
    } else if (item.type === 'link' && item.to) {
      const to = withSlash(item.to)
      entries.push({ kind: 'link', key: to, label: item.label ?? '', to, icon: item.icon, children: [], bottom: item.placement === 'bottom' })
    }
  })
  // Drop section headers with nothing under them (e.g. "Apps" when plugins are off).
  const listed = (e: Entry | undefined) => !!e && e.kind !== 'header' && !e.bottom
  return entries.filter((e, i) => e.kind !== 'header' || listed(entries[i + 1]))
}

/**
 * The nav target for the URL: the longest matching `to` across every row and sub-page, so /orders/locations (under
 * Settings) doesn't also light up Orders, and /products/42 still lights up Products.
 */
const currentTarget = (pathname: string, entries: NavEntry[]) =>
  entries
    .flatMap((e) => [e.to, ...e.children.map((c) => c.to)])
    .filter((to) => isNavActive(pathname, to))
    .sort((a, b) => b.length - a.length)[0]

/** Whether a sub-page is the one shown: settings tabs (`/settings?tab=…`) compare the query too. */
function useIsHere(current: string | undefined) {
  const { pathname, search } = useLocation()
  const tab = new URLSearchParams(search).get('tab')
  return (to: string) => (to.includes('?') ? `${pathname}${search}` === to : to === current && !(to === '/settings' && tab))
}

const groupOpen = (current: string | undefined, e: NavEntry) => e.to === current || e.children.some((c) => c.to === current)

/** Active item: a faint white lift with a white marker bar on the left edge. */
const ACTIVE =
  'relative bg-sidebar-accent text-white before:absolute before:inset-y-1.5 before:left-0 before:w-0.5 before:rounded-full before:bg-sidebar-active'

const rowClass = (current: boolean, open: boolean) =>
  cn(
    'flex h-7 w-full items-center gap-2 rounded-md px-2 text-sm font-medium transition-colors outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring',
    current
      ? cn(ACTIVE, '[&>svg]:text-sidebar-active')
      : open
        ? 'text-white hover:bg-sidebar-hover [&>svg]:text-white'
        : 'text-sidebar-foreground hover:bg-sidebar-hover hover:text-white [&>svg]:text-sidebar-muted hover:[&>svg]:text-white'
  )

function FullEntry({ entry, current: target, onNavigate }: { entry: NavEntry; current?: string; onNavigate?: () => void }) {
  const Icon = entry.icon
  const open = groupOpen(target, entry)
  const isHere = useIsHere(target)
  const current = isHere(entry.to)
  return (
    <li>
      <Link to={entry.to} onClick={onNavigate} className={rowClass(current, open)} aria-current={current ? 'page' : undefined}>
        {Icon && <Icon className="size-4 shrink-0" />}
        <span className="truncate">{entry.label}</span>
      </Link>
      {open && entry.children.length > 0 && (
        <ul className="mt-0.5 space-y-0.5">
          {entry.children.map((c) => {
            const on = isHere(c.to)
            return (
              <li key={c.to}>
                <Link
                  to={c.to}
                  onClick={onNavigate}
                  aria-current={on ? 'page' : undefined}
                  className={cn(
                    'flex h-7 items-center rounded-md pr-2 text-sm font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-sidebar-ring',
                    c.nested ? 'pl-12' : 'pl-8',
                    on ? ACTIVE : 'text-sidebar-muted hover:bg-sidebar-hover hover:text-white'
                  )}
                >
                  <span className="truncate">{c.label}</span>
                </Link>
              </li>
            )
          })}
        </ul>
      )}
    </li>
  )
}

/** How long a flyout stays open after the pointer leaves, so it can travel from the icon into the menu. */
const CLOSE_DELAY = 150

const tileClass = (active: boolean) =>
  cn(
    'mx-auto grid size-9 place-items-center rounded-md transition-colors outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring',
    active ? 'bg-sidebar-accent text-sidebar-active' : 'text-sidebar-muted hover:bg-sidebar-hover hover:text-white'
  )

/** Sidebar menus (Create, rail flyouts): near-opaque white with a light frost, so the black sidebar doesn't grey it. */
const GLASS_MENU = 'min-w-48 border border-border bg-popover/95 p-2 backdrop-blur-md backdrop-saturate-150'
const GLASS_ITEM = 'gap-2.5 px-2.5 py-2 focus:bg-fill focus:[&>svg]:text-ink'

/** Rail group: its pages open in a flyout on mouse hover (click, tap and keyboard still work). */
function MiniGroup({ entry, current }: { entry: NavEntry; current?: string }) {
  const [open, setOpen] = useState(false)
  const closeTimer = useRef<number | undefined>(undefined)
  const trigger = useRef<HTMLButtonElement>(null)
  useEffect(() => () => window.clearTimeout(closeTimer.current), [])
  const Icon = entry.icon
  const isHere = useIsHere(current)
  // The landing row is labelled by its page (Settings → General), not the group, which the menu's heading names.
  const pages = [{ to: entry.to, label: entry.landingLabel ?? entry.label, nested: false }, ...entry.children]

  const isMouse = (e: PointerEvent) => e.pointerType === 'mouse'
  const show = (e: PointerEvent) => {
    if (!isMouse(e)) return
    window.clearTimeout(closeTimer.current)
    setOpen(true)
  }
  const hide = (e: PointerEvent) => {
    if (!isMouse(e)) return
    window.clearTimeout(closeTimer.current)
    closeTimer.current = window.setTimeout(() => setOpen(false), CLOSE_DELAY)
  }

  return (
    // Not modal: the page stays usable while the pointer passes over the flyout.
    <DropdownMenu open={open} onOpenChange={setOpen} modal={false}>
      <DropdownMenuTrigger
        ref={trigger}
        className={tileClass(groupOpen(current, entry))}
        aria-label={entry.label}
        onPointerEnter={show}
        onPointerLeave={hide}
        // With a mouse, hover drives the flyout: a click only makes sure it is open instead of toggling it shut.
        onPointerDown={(e) => {
          if (!isMouse(e)) return
          e.preventDefault()
          show(e)
        }}
      >
        {Icon && <Icon className="size-4" />}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="right"
        align="start"
        sideOffset={10}
        className={GLASS_MENU}
        onPointerEnter={show}
        onPointerLeave={hide}
        // A press on the group icon is not "outside" its own flyout.
        onPointerDownOutside={(e) => {
          if (trigger.current?.contains(e.target as Node)) e.preventDefault()
        }}
      >
        <DropdownMenuLabel>{entry.label}</DropdownMenuLabel>
        {pages.map((c) => (
          <DropdownMenuItem key={c.to} asChild className={cn(GLASS_ITEM, c.nested && 'pl-6', isHere(c.to) && 'bg-fill font-medium')}>
            <Link to={c.to}>{c.label}</Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function MiniEntry({ entry, current }: { entry: NavEntry; current?: string }) {
  const Icon = entry.icon
  if (entry.children.length) return <MiniGroup entry={entry} current={current} />
  return (
    <Link to={entry.to} aria-label={entry.label} title={entry.label} className={tileClass(groupOpen(current, entry))}>
      {Icon && <Icon className="size-4" />}
    </Link>
  )
}

/** "Create" shortcuts: shown only when their list page is in the nav (so feature flags and roles still apply). */
const CREATE_ITEMS = [
  { to: '/orders/new', label: 'Order', icon: ShoppingBag, listedAt: '/orders' },
  { to: '/products/new', label: 'Product', icon: Tag, listedAt: '/products' },
  { to: '/campaigns/new', label: 'Campaign', icon: Megaphone, listedAt: '/campaigns' },
  { to: '/blog/new', label: 'Blog post', icon: FileText, listedAt: '/blog' },
]

function CreateMenu({ mini, targets, onNavigate }: { mini: boolean; targets: Set<string>; onNavigate?: () => void }) {
  const items = CREATE_ITEMS.filter((i) => targets.has(i.listedAt))
  if (!items.length) return null
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={
          mini
            ? 'mx-auto my-1 grid size-7 place-items-center rounded-md bg-card text-ink outline-none transition-colors hover:bg-fill focus-visible:ring-2 focus-visible:ring-sidebar-ring'
            : 'flex h-8 w-full items-center gap-2 rounded-md bg-card px-2.5 text-sm font-medium text-ink outline-none transition-colors hover:bg-fill focus-visible:ring-2 focus-visible:ring-sidebar-ring'
        }
        aria-label={mini ? 'Create' : undefined}
        title={mini ? 'Create' : undefined}
      >
        <Plus className="size-4 shrink-0" aria-hidden="true" />
        {!mini && (
          <>
            <span className="flex-1 text-left">Create</span>
            <ChevronDown className="size-4 shrink-0 opacity-80" aria-hidden="true" />
          </>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent side={mini ? 'right' : 'bottom'} align="start" sideOffset={mini ? 10 : 6} className={GLASS_MENU}>
        <DropdownMenuLabel className="px-2.5">Create new</DropdownMenuLabel>
        {items.map(({ to, label, icon: Icon }) => (
          <DropdownMenuItem key={to} asChild className={GLASS_ITEM}>
            <Link to={to} onClick={onNavigate}>
              <Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              {label}
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

interface SidebarProps {
  /** Icon-only rail (desktop). */
  mini?: boolean
  onToggleMini?: () => void
  /** Opens the ⌘K command palette. */
  onSearch: () => void
  /** Called after a link is followed (closes the mobile sheet). */
  onNavigate?: () => void
}

/**
 * Sidebar on the black frame: platform mark and collapse toggle, ⌘K search, the "Create" menu, nav grouped under
 * short section labels (sub-pages open under the active section, marked by a white bar), and Settings plus the store
 * menu pinned at the bottom. In mini mode a 60px icon rail with hover flyouts.
 */
export const Sidebar = ({ mini = false, onToggleMini, onSearch, onNavigate }: SidebarProps) => {
  const { pathname } = useLocation()
  const entries = useEntries()
  const main = entries.filter((e) => e.kind === 'header' || !e.bottom)
  const bottom = entries.filter((e): e is NavEntry => e.kind !== 'header' && e.bottom)
  const navEntries = entries.filter((e): e is NavEntry => e.kind !== 'header')
  const current = currentTarget(pathname, navEntries)
  const targets = new Set(navEntries.flatMap((e) => [e.to, ...e.children.map((c) => c.to)]))

  return (
    <div className="flex h-full flex-col bg-sidebar text-sidebar-foreground">
      <div className={cn('flex h-[52px] shrink-0 items-center', mini ? 'justify-center' : 'justify-between px-3.5')}>
        <Link to="/" onClick={onNavigate} aria-label="Home" className="rounded-md outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring">
          <img src="/favicon.png" alt="" className="size-7 rounded-md" />
        </Link>
        {onToggleMini && !mini && (
          <button
            type="button"
            onClick={onToggleMini}
            aria-label="Collapse sidebar"
            className="grid size-7 place-items-center rounded-md text-sidebar-muted outline-none hover:bg-sidebar-hover hover:text-white focus-visible:ring-2 focus-visible:ring-sidebar-ring"
          >
            <PanelLeft className="size-4" />
          </button>
        )}
      </div>

      {mini ? (
        <div className="space-y-1 pb-2">
          {onToggleMini && (
            <button type="button" onClick={onToggleMini} aria-label="Expand sidebar" title="Expand sidebar" className={tileClass(false)}>
              <PanelLeft className="size-4" />
            </button>
          )}
          <button type="button" onClick={onSearch} aria-label="Search" title="Search" className={tileClass(false)}>
            <Search className="size-4" />
          </button>
          <CreateMenu mini targets={targets} onNavigate={onNavigate} />
        </div>
      ) : (
        <div className="px-3.5 pb-3">
          <button
            type="button"
            onClick={onSearch}
            className="flex h-7 w-full items-center gap-2 rounded-md bg-sidebar-input px-2 text-left text-sm text-sidebar-foreground outline-none transition-colors hover:bg-sidebar-input/80 focus-visible:ring-2 focus-visible:ring-sidebar-ring"
            aria-label="Search (opens the command palette)"
          >
            <Search className="size-4 shrink-0" aria-hidden="true" />
            <span className="flex-1">Search</span>
            <kbd className="font-sans text-xs text-sidebar-foreground/80">{isMac ? '⌘K' : 'Ctrl K'}</kbd>
          </button>
          <div className="mt-2">
            <CreateMenu mini={false} targets={targets} onNavigate={onNavigate} />
          </div>
        </div>
      )}

      <nav className={cn('no-scrollbar flex flex-1 flex-col overflow-y-auto pb-2', mini ? 'gap-1' : 'px-3.5')} aria-label="Main">
        {mini ? (
          main.map((e) =>
            e.kind === 'header' ? <div key={e.key} className="mx-3 my-1.5 border-t border-sidebar-border" /> : <MiniEntry key={e.key} entry={e} current={current} />
          )
        ) : (
          <ul className="space-y-0.5">
            {main.map((e) =>
              e.kind === 'header' ? (
                <li key={e.key} className="px-2 pb-1 pt-4 text-xs font-medium text-sidebar-muted">
                  {e.label}
                </li>
              ) : (
                <FullEntry key={e.key} entry={e} current={current} onNavigate={onNavigate} />
              )
            )}
          </ul>
        )}

        {bottom.length > 0 && (
          <div className={cn('mt-auto pt-4', mini && 'space-y-1')}>
            {mini ? (
              bottom.map((e) => <MiniEntry key={e.key} entry={e} current={current} />)
            ) : (
              <ul className="space-y-0.5">
                {bottom.map((e) => (
                  <FullEntry key={e.key} entry={e} current={current} onNavigate={onNavigate} />
                ))}
              </ul>
            )}
          </div>
        )}
      </nav>

      <div className={cn('shrink-0 pb-3 pt-1', mini ? 'flex justify-center' : 'px-3.5')}>
        <AccountMenu compact={mini} />
      </div>
    </div>
  )
}
